import { type LpDealOutcome } from "@vega-api/generated/types/lpDealOutcome";
import { Skeleton } from "@vega-core/components/shadcn/skeleton";
import { m } from "@vega-core/paraglide/messages";
import {
  DateFormatString,
  formatDateByPattern,
} from "@vega-core/utils/formatDate";
import { formatMoneyShort } from "@vega-core/utils/formatMoney";
import { formatNumber, Rounding } from "@vega-core/utils/formatNumber";
import { ReportSectionMessage } from "@vega-modules/scenes/CoInvestmentsGP/pages/components/ReportSectionMessage";

function DealOutcomeBody({
  outcome,
  isError,
  isLoading,
  onRetry,
}: {
  outcome: LpDealOutcome | undefined;
  isError: boolean;
  isLoading: boolean;
  onRetry: () => void;
}) {
  if (isError) {
    return (
      <ReportSectionMessage
        dataTestId="lp-deal-outcome-error"
        message={m.common_error_somethingWentWrong()}
        onRetry={onRetry}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-5 w-32" />
      </div>
    );
  }

  if (!outcome) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-x-6 gap-y-2 body-base-regular text-text/secondary"
      data-testid="lp-deal-outcome-facts"
    >
      <span>
        {m.coInvestments_performanceReport_closedOn()}{" "}
        <span className="body-base-medium text-text/primary">
          {formatDateByPattern(
            outcome.closedOn,
            DateFormatString.US_DATE_WITH_COMMA,
          )}
        </span>
      </span>
      <span>
        {m.coInvestments_performanceReport_finalRaise()}{" "}
        <span className="body-base-medium text-text/primary">
          {formatMoneyShort(outcome.finalRaise)}
        </span>
      </span>
      <span>
        {m.coInvestments_performanceReport_yourShare()}{" "}
        <span className="body-base-medium text-text/primary">
          {`${formatNumber(outcome.yourShare, Rounding.Always1Fractions)}%`}
        </span>
      </span>
    </div>
  );
}

export function DealOutcomeSection(props: {
  outcome: LpDealOutcome | undefined;
  isError: boolean;
  isLoading: boolean;
  onRetry: () => void;
}) {
  return (
    <div className="space-y-4">
      <h2 className="heading-5">
        {m.coInvestments_performanceReport_dealOutcome()}
      </h2>
      <DealOutcomeBody {...props} />
    </div>
  );
}
